/**
 * Business_rule_canvas branch (PRISM7_CHANNEL_RULES_CANVAS_ADR.md §3) — canvas-level "incomplete
 * flow" validation, separate from the existing prose-level business_rule_validator.py (which
 * correctly treats a named-but-unresolved role as an acceptable warning for free text — that
 * stays unchanged, and is not touched here).
 *
 * An ACTION that requires approval (`meta.requires_approval`) but has no resolved recipient email
 * hard-blocks its flow as incomplete. A fallback ACTION reached when no approver could be
 * resolved (e.g. `meta.action_kind: "flag_and_draft"`, sample rule 3's own designed "if we can't
 * tell who approves, flag it and draft a reply" path) never sets `requires_approval`, so it is
 * deliberately never flagged here (ADR §3 amendment) — that is a normal, saveable flow state, not
 * something this rule accidentally forbids.
 */
import type { RuleFlowGraphNode, RuleFlow, RuleFlowRecipient } from '../../../services/channelRuleFlowsService';

/** One resolved (non-null) email is enough — matches base_shay's own "any channel member can
 * approve" model (ADR §3): there's no concept of needing every listed recipient resolved. */
export function hasResolvedRecipient(recipients: RuleFlowRecipient[] | undefined): boolean {
  return !!recipients?.some((r) => !!r.email);
}

export function isNodeIncomplete(node: Pick<RuleFlowGraphNode, 'type' | 'meta' | 'recipients'>): boolean {
  if (node.type !== 'ACTION') return false;
  if (!node.meta?.requires_approval) return false;
  return !hasResolvedRecipient(node.recipients);
}

export function isFlowIncomplete(flow: Pick<RuleFlow, 'nodes'>): boolean {
  return flow.nodes.some((n) => isNodeIncomplete(n));
}

/** Fix 14/Item 7 — the exact logical complement of isNodeIncomplete: same underlying data
 * (an ACTION's recipients), opposite condition on requires_approval, so a node can never
 * trigger both. A "missing" recipient here is non-blocking (the flow saves fine either way);
 * it's just something the canvas can point out as worth finishing. */
export function isNodeMissingRecipient(node: Pick<RuleFlowGraphNode, 'type' | 'meta' | 'recipients'>): boolean {
  if (node.type !== 'ACTION') return false;
  if (node.meta?.requires_approval) return false; // that case belongs to isNodeIncomplete instead
  return !!node.recipients?.some((r) => !r.email);
}

/** Fix 14/Item 7 — also accounts for the backend-computed node.recommendations[] (parse), not
 * just the FE-derived missing-recipient check, so a flow whose only outstanding item is a
 * validator finding still gets the rail chip. */
export function isFlowMissingDetail(flow: Pick<RuleFlow, 'nodes'>): boolean {
  return flow.nodes.some(
    (n) => isNodeMissingRecipient(n) || ((n as RuleFlowGraphNode).recommendations?.length ?? 0) > 0
  );
}

/** Tooltip/caption text for a node flagged by isNodeMissingRecipient. Returns null when the
 * node isn't flagged, so callers can use it directly as a render condition. */
export function nodeRecommendationText(
  node: Pick<RuleFlowGraphNode, 'type' | 'meta' | 'recipients'>
): string | null {
  if (!isNodeMissingRecipient(node)) return null;
  const missing = node.recipients?.find((r) => !r.email);
  const role = missing?.role ? ` for ${missing.role}` : '';
  return `Add a recipient${role} here to route this step automatically.`;
}
